import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const IntegralCalculator = () => {
  const [inputData, setInputData] = useState({
    function: '',
    a: '',
    b: '',
    n: '',
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateIntegral = () => {
    const { function: func, a, b, n } = inputData;

    // Validate input
    if (!func || !a || !b || !n) {
      setError('Please enter all required fields');
      return;
    }

    try {
      // Convert input to numbers
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      const nNum = parseInt(n, 10);

      if (isNaN(aNum) || isNaN(bNum) || isNaN(nNum)) {
        throw new Error('Please enter valid numbers for a, b, and n');
      }

      if (nNum <= 0) {
        throw new Error('Number of intervals must be a positive integer');
      }

      // Create a safe evaluation context
      const mathContext = { ...Math };
      const safeEval = new Function('x', `with(this) { return ${func} }`);

      // Calculate integral using trapezoidal rule
      const h = (bNum - aNum) / nNum;
      let integral = 0.5 * (safeEval.call(mathContext, aNum) + safeEval.call(mathContext, bNum));
      for (let i = 1; i < nNum; i++) {
        integral += safeEval.call(mathContext, aNum + i * h);
      }
      integral *= h;

      // Generate data for visualization
      const xValues = [];
      const yValues = [];
      for (let i = 0; i <= nNum; i++) {
        const x = aNum + i * h;
        xValues.push(x);
        yValues.push(safeEval.call(mathContext, x));
      }

      // Create visualization data
      const chartData = {
        labels: xValues.map(x => x.toFixed(2)),
        datasets: [{
          label: 'Function',
          data: yValues,
          fill: false,
          tension: 0.1,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          pointRadius: 3,
          pointHoverRadius: 5
        }]
      };

      // Create calculation steps
      const calculationSteps = [
        `Step 1: Use the trapezoidal rule to approximate the integral`,
        `Step 2: Divide the interval [${aNum}, ${bNum}] into ${nNum} equal parts`,
        `Step 3: Calculate function values at each interval point`,
        `Step 4: Apply the trapezoidal formula:`,
        `Integral ≈ (h/2) * [f(a) + 2Σf(x_i) + f(b)]`,
        `Step 5: Multiply by interval width h = ${(bNum - aNum) / nNum}`,
        `Step 6: Final result: ${integral.toFixed(6)}`
      ];

      setResults({
        integral: integral.toFixed(6),
        calculationSteps,
        chartData
      });

    } catch (err) {
      setError('Error calculating integral: ' + err.message);
    }
  };

  const exampleData = {
    function: 'x => x * x',
    a: 0,
    b: 1,
    n: 100,
    integral: 0.3333
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Integral Calculator | Definite Integral Tool</title>
          <meta name="description" content="Calculate the definite integral of a function using the trapezoidal rule." />
          <meta name="keywords" content="integral calculator, definite integral, trapezoidal rule, calculus" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">∫</div>
            <h1 className="text-3xl font-bold text-gray-800">Integral Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the definite integral of a function using the trapezoidal rule. This calculator approximates the area under a curve by dividing it into trapezoids.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Function (JavaScript expression):</label>
              <input
                type="text"
                name="function"
                value={inputData.function}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter function (e.g., x => x * x)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Lower Limit (a):</label>
              <input
                type="number"
                name="a"
                value={inputData.a}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter lower limit (e.g., 0)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Upper Limit (b):</label>
              <input
                type="number"
                name="b"
                value={inputData.b}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter upper limit (e.g., 1)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Intervals (n):</label>
              <input
                type="number"
                name="n"
                value={inputData.n}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter number of intervals (e.g., 100)"
                min="1"
              />
            </div>
          </div>

          <button
            onClick={calculateIntegral}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Integral
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Integral Calculation</h3>
                  <p className="text-lg">Approximated Integral: {results.integral}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Function Visualization</h3>
                  <div className="h-64 bg-white rounded-lg">
                    <Line
                      data={results.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            title: {
                              display: true,
                              text: 'Function Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'x'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Function Plot'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-pink-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the integral of x² from 0 to 1:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Function: {exampleData.function}</li>
                <li>Lower Limit: {exampleData.a}</li>
                <li>Upper Limit: {exampleData.b}</li>
                <li>Number of Intervals: {exampleData.n}</li>
                <li>Approximated Integral: {exampleData.integral}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Divide the interval [0, 1] into 100 equal parts</li>
                <li>Evaluate the function x² at each interval point</li>
                <li>Apply the trapezoidal rule formula</li>
                <li>Sum the areas of all trapezoids</li>
                <li>The result approximates the integral of x² from 0 to 1</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-pink-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Integral Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Function:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the function using JavaScript syntax (e.g., x =greater than 0 x * x)</li>
                </ul>
              </li>
              <li><strong>Set Limits:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Specify the lower and upper limits of integration</li>
                </ul>
              </li>
              <li><strong>Number of Intervals:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the number of intervals for the trapezoidal approximation</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The approximated integral value</li>
                  <li>A visualization of the function</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-pink-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use more intervals for higher precision</li>
                <li>The trapezoidal rule works best for smooth functions</li>
                <li>For functions with sharp changes, consider using more advanced methods</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Integration <span className="text-pink-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              The definite integral represents the area under a curve between two points. The trapezoidal rule is a numerical method for approximating integrals by dividing the area under the curve into trapezoids:
            </p>
            <p className="font-bold text-xl mt-4">∫ₐᵇ f(x) dx ≈ (h/2) [f(a) + 2Σf(x_i) + f(b)]</p>
            <p className="mt-2">
              Where:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>h:</strong> Width of each interval</li>
                <li><strong>x_i:</strong> Points between a and b</li>
              </ul>
            </p>
            <p className="mt-4">
              Integration has numerous applications in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Physics:</strong> Calculating work, energy, and probabilities</li>
                <li><strong>Engineering:</strong> Determining system responses</li>
                <li><strong>Economics:</strong> Analyzing consumer surplus</li>
                <li><strong>Statistics:</strong> Probability density functions</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-pink-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Choose Appropriate Intervals:</strong> More intervals provide better accuracy but require more computation</li>
              <li><strong>Understand Limitations:</strong> The trapezoidal rule may not be accurate for highly oscillatory functions</li>
              <li><strong>Compare with Analytical Solutions:</strong> Validate numerical results with exact solutions when possible</li>
              <li><strong>Use for Smooth Functions:</strong> The method works best for functions that are smooth and continuous</li>
            </ul>
            <p className="italic mt-4">
              Note: For functions with discontinuities or sharp changes, consider using more advanced numerical integration methods.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default IntegralCalculator;